import { useState, useRef, useEffect } from "react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

// ─────────────────────────────────────────────
// COLORS & GLOBALS
// ─────────────────────────────────────────────
const C = {
  bg: "#0F1923", card: "#1A2A3A", light: "#F2F0EB",
  accent: "#16697A", red: "#EA3824", redHover: "#F04D3A",
  muted: "#8D99AE", ice: "#D4E4E8", green: "#2D9B83",
};

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', 'Helvetica Neue', sans-serif; background: ${C.bg}; color: ${C.light}; }
  ::selection { background: ${C.red}; color: #fff; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: ${C.bg}; }
  ::-webkit-scrollbar-thumb { background: ${C.card}; border-radius: 2px; }
  a { text-decoration: none; color: inherit; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  @keyframes pulse { 0%,100% { opacity:.3; } 50% { opacity:1; } }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes scrollLine { 0%,100% { opacity:.2; transform:translateY(0) scaleY(1); } 50% { opacity:1; transform:translateY(6px) scaleY(1.2); } }
  .nav-link { transition: color 0.2s ease; }
  .nav-link:hover { color: ${C.light} !important; }
  .cta-btn { transition: all 0.25s ease; }
  .cta-btn:hover { background: ${C.redHover} !important; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(234,56,36,0.35); }
  .ghost-btn { transition: all 0.25s ease; }
  .ghost-btn:hover { border-color: ${C.light} !important; color: ${C.light} !important; }
  .demo-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
  .demo-card:hover { transform: translateY(-4px); box-shadow: 0 16px 48px rgba(0,0,0,0.3) !important; }
  .value-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
  .value-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(0,0,0,0.3); }
  .suggestion-btn:hover { background: rgba(22,105,122,0.2) !important; color: ${C.ice} !important; }
  .send-btn:hover:not(:disabled) { background: ${C.redHover} !important; }
  .next-btn:hover:not(:disabled) { background: ${C.redHover} !important; transform: translateY(-1px); }
  .sel-btn:hover { border-color: ${C.accent} !important; color: ${C.ice} !important; }
  input:focus, textarea:focus { outline: none !important; border-color: ${C.accent} !important; }
  .dot { display:inline-block; width:6px; height:6px; border-radius:50%; background:${C.accent}; animation: pulse 1.2s ease infinite; }
  .dot:nth-child(2) { animation-delay:.2s; }
  .dot:nth-child(3) { animation-delay:.4s; }
`;

// ─────────────────────────────────────────────
// NAV
// ─────────────────────────────────────────────
function Nav({ page, setPage }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const links = [
    { label: "Ydelser", id: "ydelser" },
    { label: "Om mig", id: "om" },
    { label: "Kontakt", id: "kontakt" },
  ];

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      padding: "0 40px", height: 64,
      background: scrolled || page !== "home" ? "rgba(15,25,35,0.95)" : "transparent",
      backdropFilter: scrolled || page !== "home" ? "blur(12px)" : "none",
      transition: "all 0.3s ease",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      borderBottom: scrolled || page !== "home" ? `1px solid rgba(141,153,174,0.1)` : "none",
    }}>
      <button onClick={() => { setPage("home"); window.scrollTo(0,0); }}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
        <span style={{ fontSize: 20, fontWeight: 500, color: C.light, letterSpacing: 0.3 }}>
          <span style={{ color: C.red }}>AI</span>'dvice
        </span>
      </button>

      {/* Desktop links */}
      <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
        {links.map(l => (
          <button key={l.id} className="nav-link"
            onClick={() => {
              setPage("home");
              setTimeout(() => document.getElementById(l.id)?.scrollIntoView({ behavior: "smooth" }), 100);
            }}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: C.muted }}>
            {l.label}
          </button>
        ))}
        <button className="nav-link"
          onClick={() => { setPage("demos"); window.scrollTo(0,0); }}
          style={{
            background: "none", border: "none", cursor: "pointer", fontSize: 14,
            color: page === "demos" ? C.light : C.muted, fontWeight: page === "demos" ? 500 : 400,
          }}>
          Demoer
        </button>
        <button className="cta-btn" onClick={() => {
          setPage("home");
          setTimeout(() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" }), 100);
        }} style={{
          padding: "8px 20px", background: C.red, color: "#fff",
          border: "none", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer",
        }}>
          Book samtale
        </button>
      </div>
    </nav>
  );
}

// ─────────────────────────────────────────────
// LANDING PAGE
// ─────────────────────────────────────────────
function LandingPage({ setPage }) {
  return (
    <div>
      {/* HERO */}
      <section style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", padding: "120px 40px 80px", position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.03, backgroundImage: `linear-gradient(${C.muted} 1px, transparent 1px), linear-gradient(90deg, ${C.muted} 1px, transparent 1px)`, backgroundSize: "80px 80px" }} />
        <div style={{ maxWidth: 720, position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.red, letterSpacing: 3, textTransform: "uppercase", marginBottom: 24, animation: "fadeUp 0.6s ease both" }}>
            AI-konsulentvirksomhed · Haderslev
          </div>
          <h1 style={{ fontSize: "clamp(42px, 6vw, 76px)", fontWeight: 500, lineHeight: 1.06, marginBottom: 28, letterSpacing: -1, animation: "fadeUp 0.6s ease 0.1s both" }}>
            Sæt din<br />virksomhed <span style={{ color: C.red }}>fri</span>
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.7, color: C.muted, maxWidth: 520, marginBottom: 40, animation: "fadeUp 0.6s ease 0.2s both" }}>
            Din virksomhed har potentiale der ikke bliver udnyttet. Vi finder ud af hvor — og integrerer AI præcis dér, hvor det frigør mest tid og skaber størst værdi.
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", animation: "fadeUp 0.6s ease 0.3s both" }}>
            <button className="cta-btn" onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })} style={{
              padding: "14px 32px", background: C.red, color: "#fff",
              border: "none", borderRadius: 6, fontSize: 15, fontWeight: 500, cursor: "pointer",
            }}>
              Book en uforpligtende samtale
            </button>
            <button className="ghost-btn" onClick={() => { setPage("demos"); window.scrollTo(0,0); }} style={{
              padding: "14px 32px", background: "transparent",
              border: `1px solid rgba(141,153,174,0.3)`, color: C.muted,
              borderRadius: 6, fontSize: 15, cursor: "pointer",
            }}>
              Se demoer →
            </button>
          </div>
        </div>
        <div style={{ position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)" }}>
          <div style={{ width: 1, height: 48, background: `linear-gradient(${C.red}, transparent)`, animation: "scrollLine 2s ease infinite" }} />
        </div>
      </section>

      {/* STATEMENT */}
      <section style={{ padding: "100px 40px", borderTop: `1px solid rgba(141,153,174,0.08)` }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <p style={{ fontSize: "clamp(22px, 3.2vw, 34px)", fontWeight: 300, lineHeight: 1.55, color: C.ice }}>
            "Vi starter aldrig med teknologien. Vi starter med{" "}
            <span style={{ color: C.red, fontWeight: 500 }}>din forretning</span>{" "}
            — dine processer, dine flaskehalse, dit uudnyttede potentiale. Først når vi forstår helheden, ved vi hvor AI skaber reel værdi."
          </p>
          <div style={{ marginTop: 20, fontSize: 14, color: C.muted }}>— Rune Apollo, grundlægger</div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="ydelser" style={{ padding: "100px 40px", borderTop: `1px solid rgba(141,153,174,0.08)` }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.accent, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>Sådan arbejder vi</div>
          <h2 style={{ fontSize: 36, fontWeight: 500, marginBottom: 14, lineHeight: 1.2 }}>Fra indsigt til resultat</h2>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: C.muted, maxWidth: 600, marginBottom: 48 }}>
            Vi tager udgangspunkt i din virksomhed som den er i dag. Ingen standardløsninger — kun AI-integration der passer til dine processer, dine mennesker og dine mål.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            {[
              { tag: "Forarbejdet", title: "1. Modenhedsvurdering", desc: "Vi går i dybden med dine arbejdsgange og dit tidsforbrug. Sammen finder vi de steder hvor AI kan flytte mest — hurtigt og konkret.", icon: "◎" },
              { tag: "Quick wins", title: "2. Hurtig værdi", desc: "Vi starter med de lavthængende frugter — rutineopgaver der æder din tid uden at skabeværdi. Du mærker forskel inden for uger.", icon: "⬡" },
              { tag: "Langsigtet vækst", title: "3. Skalering & strategi", desc: "Når de første resultater er i hus, bygger vi videre. AI bliver en varig konkurrencefordel.", icon: "△" },
            ].map((s, i) => (
              <div key={i} className="value-card" style={{
                background: C.card, borderRadius: 12, padding: 32, position: "relative",
                border: `1px solid rgba(141,153,174,0.08)`,
              }}>
                <div style={{ position: "absolute", top: 20, right: 20, fontSize: 28, color: C.accent, opacity: 0.2 }}>{s.icon}</div>
                <div style={{ fontSize: 11, fontWeight: 500, color: C.accent, letterSpacing: 2, textTransform: "uppercase", marginBottom: 14 }}>{s.tag}</div>
                <h3 style={{ fontSize: 19, fontWeight: 500, marginBottom: 10 }}>{s.title}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: C.muted }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section style={{ padding: "80px 40px", borderTop: `1px solid rgba(141,153,174,0.08)` }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 40 }}>
          {[
            { num: "100+", label: "timer frigjort per kunde om måneden" },
            { num: "3", label: "uger til du mærker de første resultater" },
            { num: "0", label: "standardløsninger — alt tilpasses din forretning" },
            { num: "1", label: "mål: at din virksomhed lever sit fulde potentiale" },
          ].map((v, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, fontWeight: 500, color: C.red, lineHeight: 1 }}>{v.num}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>{v.label}</div>
            </div>
          ))}
        </div>
      </section>
{/* GEO */}
<section style={{ padding: "100px 40px", borderTop: `1px solid rgba(141,153,174,0.08)`, background: "#0d1720" }}>
  <div style={{ maxWidth: 900, margin: "0 auto" }}>
    <div style={{ fontSize: 12, fontWeight: 500, color: C.accent, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>Ny ydelse</div>
    <h2 style={{ fontSize: 36, fontWeight: 500, marginBottom: 14, lineHeight: 1.2 }}>
      Bliv fundet af AI — ikke kun af Google
    </h2>
    <p style={{ fontSize: 16, lineHeight: 1.75, color: C.muted, maxWidth: 640, marginBottom: 48 }}>
      Når dine kunder søger efter hjælp, spørger de i stigende grad ChatGPT, Claude eller Google AI — ikke kun den klassiske søgemaskine. <strong style={{ color: C.ice }}>Generative Engine Optimization (GEO)</strong> handler om at optimere din virksomhed til at blive fundet og citeret af AI-systemer. Det er det nye SEO — og de fleste virksomheder er slet ikke klar.
    </p>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
      {[
        { icon: "◉", title: "AI svarer med dit navn", desc: "Når en potentiel kunde spørger ChatGPT om den bedste leverandør i din branche — dukker din virksomhed op? Med GEO sikrer vi at AI-systemer kender, forstår og anbefaler dig." },
        { icon: "◈", title: "Indhold der taler til AI", desc: "AI-søgemaskiner prioriterer klart struktureret, autoritativt indhold. Vi optimerer din tekst, dine metadata og din struktur så AI'en forstår præcis hvad du tilbyder og hvem du hjælper." },
        { icon: "◇", title: "Fremtidssikret synlighed", desc: "Traditionel SEO er ikke nok længere. GEO er den næste generation af digital synlighed — og at komme tidligt i gang giver et konkurrencefortrin der er svært at indhente." },
      ].map((s, i) => (
        <div key={i} className="value-card" style={{
          background: C.card, borderRadius: 12, padding: "28px 24px",
          border: `1px solid rgba(141,153,174,0.08)`, position: "relative",
        }}>
          <div style={{ fontSize: 28, color: C.red, opacity: 0.6, marginBottom: 12 }}>{s.icon}</div>
          <h3 style={{ fontSize: 18, fontWeight: 500, marginBottom: 10 }}>{s.title}</h3>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: C.muted }}>{s.desc}</p>
        </div>
      ))}
    </div>
    <div style={{ marginTop: 36, padding: "20px 24px", background: C.card, borderRadius: 12, border: `1px solid rgba(234,56,36,0.2)`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>Er din virksomhed synlig for AI?</div>
        <div style={{ fontSize: 13, color: C.muted }}>Vi laver en gratis GEO-analyse som en del af modenhedsvurderingen.</div>
      </div>
      <button className="cta-btn" onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })} style={{
        padding: "11px 24px", background: C.red, color: "#fff",
        border: "none", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer",
      }}>
        Book analyse →
      </button>
    </div>
  </div>
</section>


      {/* DEMOS TEASER */}
      <section style={{ padding: "80px 40px", borderTop: `1px solid rgba(141,153,174,0.08)` }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.accent, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>Se det i praksis</div>
          <h2 style={{ fontSize: 32, fontWeight: 500, marginBottom: 14 }}>Konkrete demoer</h2>
          <p style={{ fontSize: 15, color: C.muted, maxWidth: 500, margin: "0 auto 32px" }}>
            Tre eksempler på hvad AI kan gøre for en SMV — alle bygget og skræddersyet af AI'dvice.
          </p>
          <button className="cta-btn" onClick={() => { setPage("demos"); window.scrollTo(0,0); }} style={{
            padding: "13px 32px", background: C.red, color: "#fff",
            border: "none", borderRadius: 6, fontSize: 15, fontWeight: 500, cursor: "pointer",
          }}>
            Se demoer →
          </button>
        </div>
      </section>

      {/* ABOUT */}
      <section id="om" style={{ padding: "100px 40px", borderTop: `1px solid rgba(141,153,174,0.08)` }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.accent, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>Om mig</div>
          <h2 style={{ fontSize: 36, fontWeight: 500, marginBottom: 28, lineHeight: 1.2 }}>Rune Apollo</h2>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: C.muted, marginBottom: 18 }}>
            15+ års erfaring med at drive projekter fra idé til eksekvering — på tværs af musik, mode, startup og erhvervsfremme. Jeg har mentoreret 75+ iværksættere og set hvad der sker når den rigtige indsigt møder det rigtige værktøj.
          </p>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: C.muted, marginBottom: 18 }}>
            Min styrke er systemtænkning: at se helheden i en virksomhed — hvor den er, og hvor den kan hen. AI gav mig muligheden for at eksekvere idéer uden store teams og investorer. Den frihed vil jeg give videre.
          </p>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: C.ice }}>
            Jeg møder mine kunder i øjenhøjde. Uden indsigt i din forretning kan jeg ikke skabe værdi — derfor starter ethvert samarbejde med at jeg lytter.
          </p>
          <div style={{ marginTop: 32, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {["Systemtænkning", "Art Direction", "Projektledelse", "AI-integration", "Bæredygtighed"].map((t, i) => (
              <span key={i} style={{ padding: "5px 14px", borderRadius: 20, border: `1px solid rgba(22,105,122,0.4)`, fontSize: 13, color: C.accent }}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PAKKER */}
      <section style={{ padding: "100px 40px", borderTop: `1px solid rgba(141,153,174,0.08)`, background: "#0d1720" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.accent, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10, textAlign: "center" }}>Pakker</div>
          <h2 style={{ fontSize: 36, fontWeight: 500, marginBottom: 48, textAlign: "center" }}>Vælg det der passer til dig</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
            {[
              { name: "Indsigt", price: "15.000 kr", tag: "", items: ["Modenhedsvurdering", "2 dages analyse", "Prioriteret handlingsplan", "ROI-estimat"], featured: false },
              { name: "Fremdrift", price: "45.000 kr", tag: "Populær", items: ["Alt i Indsigt +", "5 dages implementation", "AI-integration af 2-3 processer", "Oplæring af teamet"], featured: true },
              { name: "Transformation", price: "Fra 80.000 kr", tag: "", items: ["Alt i Fremdrift +", "Fuld AI-strategi", "Løbende optimering", "Kvartalsvis opfølgning"], featured: false },
            ].map((p, i) => (
              <div key={i} style={{
                background: p.featured ? C.card : "rgba(26,42,58,0.5)",
                borderRadius: 12, padding: "28px 24px",
                border: p.featured ? `1px solid ${C.red}` : `1px solid rgba(141,153,174,0.1)`,
                position: "relative",
              }}>
                {p.tag && (
                  <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: C.red, color: "#fff", fontSize: 11, fontWeight: 500, padding: "3px 12px", borderRadius: 20 }}>{p.tag}</div>
                )}
                <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 6 }}>{p.name}</div>
                <div style={{ fontSize: 22, fontWeight: 500, color: C.red, marginBottom: 20 }}>{p.price}</div>
                {p.items.map((item, j) => (
                  <div key={j} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8, fontSize: 13, color: C.muted }}>
                    <span style={{ color: C.accent, marginTop: 1 }}>✓</span>{item}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <p style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: C.muted }}>
            Fleksibel rådgivning: 6.000–8.000 kr/dag · Alle priser ekskl. moms
          </p>
        </div>
      </section>

      {/* CTA */}
      <section id="kontakt" style={{ padding: "100px 40px", borderTop: `1px solid rgba(141,153,174,0.08)`, textAlign: "center" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ fontSize: 36, fontWeight: 500, marginBottom: 18 }}>Hvad kunne din virksomhed opnå?</h2>
          <p style={{ fontSize: 16, color: C.muted, lineHeight: 1.7, marginBottom: 36 }}>
            Det finder vi ud af sammen. En uforpligtende samtale hvor vi taler om dine processer, dine udfordringer — og hvor AI kan gøre den største forskel.
          </p>
          <a href="mailto:runeapollo@mail.com" className="cta-btn" style={{
            display: "inline-block", padding: "15px 40px",
            background: C.red, color: "#fff", borderRadius: 6,
            fontSize: 16, fontWeight: 500,
          }}>
            Tag kontakt
          </a>
          <div style={{ marginTop: 16, fontSize: 13, color: C.muted }}>
            runeapollo@mail.com · +45 2265 5321 · Haderslev
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: "32px 40px", borderTop: `1px solid rgba(141,153,174,0.08)`, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: C.muted }}>
        <div><span style={{ color: C.red }}>AI</span>'dvice · Haderslev, Danmark</div>
        <div>© 2026 AI'dvice</div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────
// DEMOS PAGE
// ─────────────────────────────────────────────
function DemosPage() {
  const [active, setActive] = useState(null);

  const demos = [
    { id: "chatbot", title: "AI Kundeservice", subtitle: "Chatbot-assistent", desc: "En AI-assistent konfigureret til en tømrervirksomhed. I dit salgsmøde viser du kunden hvordan en bot til deres branche kan se ud — skræddersyet på én dag.", icon: "💬", tag: "Kundeservice & FAQ" },
    { id: "rapport", title: "AI-modenhedsrapport", subtitle: "Rapport-generator", desc: "Kunden udfylder et simpelt 3-trins skema om deres virksomhed. AI'dvice genererer en skræddersyet analyse med konkrete anbefalinger og ROI-estimat.", icon: "📊", tag: "Analyse & strategi" },
    { id: "dashboard", title: "Data Dashboard", subtitle: "Datavisualisering", desc: "Rå forretningsdata omdannet til et overskueligt dashboard med KPI'er, tidsbesparelser og ROI-kurve. Viser kunden hvad de kan forvente at se.", icon: "📈", tag: "Visualisering & rapportering" },
  ];

  return (
    <div style={{ padding: "100px 40px 60px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: C.accent, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>Demo-portefølje</div>
        <h1 style={{ fontSize: 40, fontWeight: 500, marginBottom: 14 }}>Se AI i praksis</h1>
        <p style={{ fontSize: 16, color: C.muted, maxWidth: 540, marginBottom: 56, lineHeight: 1.7 }}>
          Tre konkrete eksempler på hvad AI kan gøre for en SMV. Alle demoer er fuldt fungerende og kan tilpasses din kundes branche på kort tid.
        </p>

        {!active ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
            {demos.map((d) => (
              <div key={d.id} className="demo-card" onClick={() => setActive(d.id)} style={{
                background: C.card, borderRadius: 14, padding: "32px 28px",
                border: `1px solid rgba(141,153,174,0.08)`, cursor: "pointer",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              }}>
                <div style={{ fontSize: 36, marginBottom: 16 }}>{d.icon}</div>
                <div style={{ fontSize: 11, color: C.accent, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>{d.tag}</div>
                <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 6 }}>{d.title}</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.6 }}>{d.desc}</div>
                <div style={{ fontSize: 13, color: C.red, fontWeight: 500 }}>Åbn demo →</div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <button onClick={() => setActive(null)} style={{
              background: "none", border: `1px solid rgba(141,153,174,0.2)`, color: C.muted,
              borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer", marginBottom: 28,
            }}>
              ← Tilbage til demoer
            </button>
            {active === "chatbot" && <ChatbotDemo />}
            {active === "rapport" && <RapportDemo />}
            {active === "dashboard" && <DashboardDemo />}
          </div>
        )}

        <div style={{ marginTop: 60, padding: "28px 32px", background: C.card, borderRadius: 14, border: `1px solid rgba(141,153,174,0.08)`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>Vil du have en demo tilpasset din branche?</div>
            <div style={{ fontSize: 13, color: C.muted }}>Alle demoer kan skræddersyes til din kundes specifikke situation på 1-2 dage.</div>
          </div>
          <a href="mailto:runeapollo@mail.com" className="cta-btn" style={{
            padding: "12px 24px", background: C.red, color: "#fff",
            borderRadius: 8, fontSize: 14, fontWeight: 500, display: "inline-block",
          }}>
            Kontakt AI'dvice
          </a>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DEMO 1: CHATBOT
// ─────────────────────────────────────────────
const SYSTEM_PROMPT = `Du er en hjælpsom AI-assistent for Hansens Tømrerforretning i Odense.
Du besvarer spørgsmål om virksomhedens ydelser, åbningstider, priser og processer.
Virksomhedsinfo:
- Navn: Hansens Tømrerforretning
- Grundlagt: 1987
- Specialer: Tagarbejde, udestuer, carporte, køkkenrenovering
- Åbningstider: Man-fre 7-16, lørdag 8-12
- Kontakt: 66 12 34 56 | info@hansenstomrer.dk
- Priser: Gratis besigtigelse, tilbud sendes inden 5 hverdage
- Garanti: 5 års arbejdsgaranti på alle opgaver
Svar altid på dansk. Vær venlig, konkret og hjælpsom.`;

async function askClaude(messages) {
  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, system: SYSTEM_PROMPT, messages }),
  });
  const data = await response.json();
  return data.content?.[0]?.text || "Beklager, prøv igen.";
}

function ChatbotDemo() {
  const [messages, setMessages] = useState([{ role: "assistant", content: "Hej! Jeg er her for at hjælpe dig med spørgsmål om Hansens Tømrerforretning. Hvad kan jeg gøre for dig?" }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const suggestions = ["Hvad koster et nyt tag?", "Hvornår har I åbent?", "Kan I lave en udestue?", "Hvor hurtigt kan I komme?"];

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    const updated = [...messages, { role: "user", content: msg }];
    setMessages(updated);
    setLoading(true);
    const reply = await askClaude(updated.map(m => ({ role: m.role, content: m.content })));
    setMessages([...updated, { role: "assistant", content: reply }]);
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ marginBottom: 12, fontSize: 13, color: C.muted }}>
        Demo: AI-kundeservice for <strong style={{ color: C.light }}>Hansens Tømrerforretning</strong> — tilpasses din kundes branche på 1 dag.
      </div>
      <div style={{ background: C.card, borderRadius: 16, overflow: "hidden", border: `1px solid rgba(141,153,174,0.1)` }}>
        <div style={{ padding: "14px 20px", background: C.bg, borderBottom: `1px solid rgba(141,153,174,0.1)`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🔨</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Hansens Assistent</div>
            <div style={{ fontSize: 11, color: C.green }}>● Online nu</div>
          </div>
        </div>
        <div style={{ height: 300, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", animation: "fadeUp 0.3s ease" }}>
              <div style={{
                maxWidth: "80%", padding: "10px 14px",
                borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                background: m.role === "user" ? C.red : C.bg,
                color: C.light, fontSize: 14, lineHeight: 1.6,
                border: m.role === "assistant" ? `1px solid rgba(141,153,174,0.1)` : "none",
              }}>{m.content}</div>
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex" }}>
              <div style={{ padding: "12px 16px", borderRadius: "16px 16px 16px 4px", background: C.bg, border: `1px solid rgba(141,153,174,0.1)`, display: "flex", gap: 4 }}>
                <div className="dot" /><div className="dot" /><div className="dot" />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        {messages.length < 3 && (
          <div style={{ padding: "0 16px 10px", display: "flex", flexWrap: "wrap", gap: 6 }}>
            {suggestions.map((s, i) => (
              <button key={i} className="suggestion-btn" onClick={() => send(s)} style={{ padding: "5px 12px", borderRadius: 20, border: `1px solid rgba(22,105,122,0.4)`, background: "transparent", color: C.muted, fontSize: 12, cursor: "pointer", transition: "all 0.15s" }}>{s}</button>
            ))}
          </div>
        )}
        <div style={{ padding: "10px 14px", borderTop: `1px solid rgba(141,153,174,0.1)`, display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea ref={inputRef} rows={1} value={input}
            onChange={e => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px"; }}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Stil et spørgsmål..."
            style={{ flex: 1, background: C.bg, border: `1px solid rgba(141,153,174,0.2)`, borderRadius: 10, padding: "9px 12px", color: C.light, fontSize: 14, resize: "none", lineHeight: 1.5, fontFamily: "inherit", minHeight: 40, transition: "border-color 0.2s" }}
          />
          <button className="send-btn" onClick={() => send()} disabled={!input.trim() || loading} style={{ width: 40, height: 40, borderRadius: 10, background: input.trim() && !loading ? C.red : "rgba(141,153,174,0.2)", border: "none", cursor: input.trim() && !loading ? "pointer" : "default", fontSize: 18, color: "#fff", transition: "all 0.2s", flexShrink: 0 }}>↑</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DEMO 2: RAPPORT GENERATOR
// ─────────────────────────────────────────────
async function generateReport(f) {
  const prompt = `Du er en professionel AI-konsulent. Generer en struktureret AI-modenhedsrapport på dansk baseret på:
Virksomhed: ${f.name} | Branche: ${f.industry} | Medarbejdere: ${f.employees}
Tidsspilde: ${f.timeWaste} | AI-erfaring: ${f.aiExperience} | Mål: ${f.goal}

Skriv en rapport med: 1. Opsummering (2-3 sætn.) 2. Nuværende situation 3. Top 3 AI-muligheder 4. Forventet ROI 5. Næste skridt. Max 350 ord. Konkret og handlingsorienteret.`;
  const res = await fetch("https://api.anthropic.com/v1/messages", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, messages: [{ role: "user", content: prompt }] }) });
  const data = await res.json();
  return data.content?.[0]?.text || "Fejl.";
}

const industries = ["Produktion", "Service", "Handel & retail", "Bygge & anlæg", "Rådgivning", "Sundhed", "Transport", "Andet"];
const empRanges = ["1-5", "6-15", "16-30", "31-50", "50+"];
const aiLevels = ["Ingen erfaring", "Har prøvet ChatGPT", "Bruger AI-værktøjer", "AI integreret i processer"];

function SelBtn({ value, current, onClick }) {
  return (
    <button onClick={onClick} className="sel-btn" style={{
      padding: "7px 14px", borderRadius: 8,
      border: `1px solid ${current === value ? C.red : "rgba(141,153,174,0.2)"}`,
      background: current === value ? "rgba(234,56,36,0.08)" : "transparent",
      color: current === value ? C.light : C.muted,
      fontSize: 13, cursor: "pointer", transition: "all 0.15s",
    }}>{value}</button>
  );
}

function RapportDemo() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", industry: "", employees: "", timeWaste: "", aiExperience: "", goal: "" });
  const [report, setReport] = useState("");
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const go = async () => {
    setLoading(true); setStep(3);
    const r = await generateReport(form);
    setReport(r); setLoading(false);
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <div style={{ marginBottom: 12, fontSize: 13, color: C.muted }}>Demo: AI-modenhedsrapport — udfyld skemaet og få en skræddersyet analyse.</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
        {[1,2,3].map(n => <div key={n} style={{ height: 3, width: 60, borderRadius: 2, background: step >= n ? C.red : "rgba(141,153,174,0.2)", transition: "background 0.3s" }} />)}
      </div>
      <div style={{ background: C.card, borderRadius: 14, padding: "28px 24px", border: `1px solid rgba(141,153,174,0.08)` }}>
        {step === 1 && (
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 500, marginBottom: 22 }}>Om din virksomhed</h3>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 6 }}>Virksomhedens navn</label>
              <input value={form.name} onChange={e => upd("name", e.target.value)} placeholder="fx Hansens Tømrerforretning" style={{ width: "100%", padding: "9px 12px", border: `1px solid rgba(141,153,174,0.15)`, borderRadius: 8, fontSize: 14, color: C.light, fontFamily: "inherit", background: C.bg, transition: "border-color 0.2s" }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 8 }}>Branche</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{industries.map(i => <SelBtn key={i} value={i} current={form.industry} onClick={() => upd("industry", i)} />)}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 8 }}>Medarbejdere</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{empRanges.map(e => <SelBtn key={e} value={e} current={form.employees} onClick={() => upd("employees", e)} />)}</div>
            </div>
            <button className="next-btn" onClick={() => setStep(2)} disabled={!form.name || !form.industry || !form.employees} style={{ width: "100%", padding: "11px", background: form.name && form.industry && form.employees ? C.red : "rgba(141,153,174,0.25)", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: "pointer", transition: "all 0.2s" }}>Næste →</button>
          </div>
        )}
        {step === 2 && (
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 500, marginBottom: 22 }}>Jeres situation</h3>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 8 }}>Største tidsspilde?</label>
              <textarea value={form.timeWaste} onChange={e => upd("timeWaste", e.target.value)} rows={2} placeholder="fx manuelle rapporter, dataindtastning..." style={{ width: "100%", padding: "9px 12px", border: `1px solid rgba(141,153,174,0.15)`, borderRadius: 8, fontSize: 14, color: C.light, fontFamily: "inherit", background: C.bg, resize: "none", lineHeight: 1.5, transition: "border-color 0.2s" }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 8 }}>AI-erfaring</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{aiLevels.map(l => <SelBtn key={l} value={l} current={form.aiExperience} onClick={() => upd("aiExperience", l)} />)}</div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 8 }}>Mål med AI</label>
              <textarea value={form.goal} onChange={e => upd("goal", e.target.value)} rows={2} placeholder="fx spare tid, vækste uden at ansætte..." style={{ width: "100%", padding: "9px 12px", border: `1px solid rgba(141,153,174,0.15)`, borderRadius: 8, fontSize: 14, color: C.light, fontFamily: "inherit", background: C.bg, resize: "none", lineHeight: 1.5, transition: "border-color 0.2s" }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: "11px", background: "transparent", color: C.muted, border: `1px solid rgba(141,153,174,0.2)`, borderRadius: 10, fontSize: 14, cursor: "pointer" }}>← Tilbage</button>
              <button className="next-btn" onClick={go} disabled={!form.timeWaste || !form.aiExperience || !form.goal} style={{ flex: 2, padding: "11px", background: form.timeWaste && form.aiExperience && form.goal ? C.red : "rgba(141,153,174,0.25)", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: "pointer", transition: "all 0.2s" }}>Generer rapport ✦</button>
            </div>
          </div>
        )}
        {step === 3 && (
          loading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", border: `3px solid rgba(234,56,36,0.2)`, borderTopColor: C.red, animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
              <div style={{ fontSize: 14, color: C.muted }}>Analyserer {form.name}...</div>
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                <h3 style={{ fontSize: 17, fontWeight: 500 }}>Din rapport</h3>
                <div style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(234,56,36,0.1)", color: C.red, fontWeight: 500 }}>AI'dvice</div>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8, color: "#ccc", whiteSpace: "pre-wrap", borderLeft: `3px solid ${C.red}`, paddingLeft: 14, marginBottom: 20 }}>{report}</div>
              <div style={{ padding: "12px 16px", background: C.bg, borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>Vil du have en fuld analyse?</div>
                  <div style={{ fontSize: 12, color: C.muted }}>Modenhedsvurdering · 15.000 kr</div>
                </div>
                <a href="mailto:runeapollo@mail.com" style={{ padding: "8px 14px", background: C.red, color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 500 }}>Kontakt</a>
              </div>
              <button onClick={() => { setStep(1); setForm({ name:"",industry:"",employees:"",timeWaste:"",aiExperience:"",goal:"" }); setReport(""); }} style={{ width: "100%", padding: "9px", background: "transparent", color: C.muted, border: `1px solid rgba(141,153,174,0.2)`, borderRadius: 10, fontSize: 13, cursor: "pointer" }}>Start forfra</button>
            </div>
          )
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DEMO 3: DASHBOARD
// ─────────────────────────────────────────────
const ROI_DATA = { virksomhed: "Andersen Byggeservice ApS", periode: "Jan – Jun 2025",
  uger: [{ uge: "Uge 1", manuelt: 42, ai: 12 }, { uge: "Uge 2", manuelt: 38, ai: 10 }, { uge: "Uge 3", manuelt: 45, ai: 11 }, { uge: "Uge 4", manuelt: 40, ai: 9 }, { uge: "Uge 5", manuelt: 44, ai: 10 }, { uge: "Uge 6", manuelt: 35, ai: 8 }],
  fordeling: [{ name: "Tilbudsgivning", value: 28, color: C.red }, { name: "Rapportering", value: 22, color: C.accent }, { name: "Kundekorr.", value: 18, color: C.green }, { name: "Dataindtastning", value: 15, color: "#E8C468" }, { name: "Andet", value: 17, color: C.muted }],
  roi: [{ mdr: "Jan", kr: 24000 }, { mdr: "Feb", kr: 31000 }, { mdr: "Mar", kr: 29000 }, { mdr: "Apr", kr: 38000 }, { mdr: "Maj", kr: 42000 }, { mdr: "Jun", kr: 45000 }],
};

const TT = ({ active, payload, label }) => active && payload?.length ? (
  <div style={{ background: C.bg, border: `1px solid rgba(141,153,174,0.2)`, borderRadius: 8, padding: "8px 12px" }}>
    <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{label}</div>
    {payload.map((p, i) => <div key={i} style={{ fontSize: 13, color: p.color || C.light }}>{p.name}: {p.value > 999 ? `${(p.value/1000).toFixed(0)}k kr` : `${p.value}t`}</div>)}
  </div>
) : null;

function DashboardDemo() {
  const [hov, setHov] = useState(null);
  const totalSparet = ROI_DATA.uger.reduce((s, d) => s + (d.manuelt - d.ai), 0);
  const totalKr = ROI_DATA.roi.reduce((s, d) => s + d.kr, 0);
  const kpis = [
    { l: "Timer sparet", v: `${totalSparet}t`, s: "over 6 uger" },
    { l: "Samlet besparelse", v: `${(totalKr/1000).toFixed(0)}k kr`, s: "jan–jun" },
    { l: "ROI", v: "12x", s: "af investering" },
    { l: "Timer/uge", v: `${(totalSparet/6).toFixed(0)}t`, s: "gennemsnit" },
  ];
  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 13, color: C.muted }}>Demo: Live data dashboard for <strong style={{ color: C.light }}>{ROI_DATA.virksomhed}</strong> · {ROI_DATA.periode}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
        {kpis.map((k, i) => (
          <div key={i} style={{ background: C.card, borderRadius: 10, padding: "16px", border: `1px solid rgba(141,153,174,0.08)` }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>{k.l}</div>
            <div style={{ fontSize: 26, fontWeight: 500, color: C.red, lineHeight: 1 }}>{k.v}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{k.s}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div style={{ background: C.card, borderRadius: 10, padding: "18px", border: `1px solid rgba(141,153,174,0.08)` }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Timer: Manuel vs AI</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={ROI_DATA.uger} barGap={4}>
              <XAxis dataKey="uge" tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
              <Tooltip content={<TT />} />
              <Bar dataKey="manuelt" name="Manuelt" fill={C.muted} radius={[3,3,0,0]} />
              <Bar dataKey="ai" name="Med AI" fill={C.red} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: C.card, borderRadius: 10, padding: "18px", border: `1px solid rgba(141,153,174,0.08)` }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Tidsforbrug fordeling</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ResponsiveContainer width={120} height={120}>
              <PieChart>
                <Pie data={ROI_DATA.fordeling} cx="50%" cy="50%" innerRadius={32} outerRadius={55} dataKey="value" stroke="none" onMouseEnter={(_, i) => setHov(i)} onMouseLeave={() => setHov(null)}>
                  {ROI_DATA.fordeling.map((e, i) => <Cell key={i} fill={e.color} opacity={hov === null || hov === i ? 1 : 0.3} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1 }}>
              {ROI_DATA.fordeling.map((d, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, opacity: hov === null || hov === i ? 1 : 0.3 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: d.color }} />
                  <div style={{ fontSize: 11, color: C.muted, flex: 1 }}>{d.name}</div>
                  <div style={{ fontSize: 11, fontWeight: 500 }}>{d.value}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div style={{ background: C.card, borderRadius: 10, padding: "18px", border: `1px solid rgba(141,153,174,0.08)`, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Månedlig besparelse</div>
          <div style={{ fontSize: 12, color: C.green, fontWeight: 500 }}>↑ +88% siden jan</div>
        </div>
        <ResponsiveContainer width="100%" height={130}>
          <LineChart data={ROI_DATA.roi}>
            <XAxis dataKey="mdr" tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${v/1000}k`} />
            <Tooltip content={<TT />} />
            <Line type="monotone" dataKey="kr" name="Besparelse" stroke={C.red} strokeWidth={2.5} dot={{ fill: C.red, r: 4 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("home");
  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <Nav page={page} setPage={setPage} />
      {page === "home" && <LandingPage setPage={setPage} />}
      {page === "demos" && <DemosPage />}
    </>
  );
}
